// src/components/ui/DocumentationPage.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Divider,
  Alert,
  Paper,
} from '@mui/material';
import { Section } from './Section';
import { FeatureCard } from './FeatureCard';
import { StatsCard } from './StatsCard';
import { AnimatedBackground } from './AnimatedBackground';
import { ProgressBar } from './ProgressBar';
import { Input } from './Input';
import { Button } from './Button';
import CodeIcon from '@mui/icons-material/Code';
import PaletteIcon from '@mui/icons-material/Palette';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

const DocumentationPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Add click handlers for buttons
  const handleGetStartedClick = () => {
    setActiveTab(0); // Switch to Getting Started tab
  };

  const handleViewComponentsClick = () => {
    setActiveTab(1); // Switch to Components tab
  };

  const handleGitHubClick = () => {
    // Open GitHub repository in new tab
    window.open('https://github.com/zolang-nexora/ui-library', '_blank');
  };

  const tabs = [
    { label: 'Getting Started' },
    { label: 'Components' },
    { label: 'Theming' },
    { label: 'Examples' },
  ];

  const components = [
    {
      name: 'Button',
      description: 'Enhanced button with animations and gradients',
      props: ['variant', 'animate', 'glow', 'size'],
      usage: `
<Button variant="contained" animate glow>
  Click Me
</Button>
      `,
    },
    {
      name: 'FeatureCard',
      description: 'Card component for showcasing features',
      props: ['icon', 'title', 'description', 'gradient'],
      usage: `
<FeatureCard
  icon={<CodeIcon />}
  title="Feature"
  description="Description"
  gradient
/>
      `,
    },
    {
      name: 'Section',
      description: 'Layout section with optional title and background',
      props: ['title', 'subtitle', 'background', 'spacing'],
      usage: `
<Section title="Welcome" subtitle="Subtitle" background="gradient">
  <YourContent />
</Section>
      `,
    },
    {
      name: 'StatsCard',
      description: 'Card for displaying statistics with animations',
      props: ['number', 'label', 'suffix', 'animate'],
      usage: `
<StatsCard number="150" suffix="+" label="Projects" animate />
      `,
    },
  ];

  return (
    <>
      <AnimatedBackground />
      
      {/* Header Section */}
      <Section background="gradient" spacing={8}>
        <Box sx={{ textAlign: 'center' }}>
          <Chip 
            label="v1.0.0" 
            sx={{ 
              background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
              color: 'white',
              mb: 2,
              fontWeight: 'bold',
            }}
          />
          <Typography 
            variant="h1" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '2.5rem', md: '4rem' },
              mb: 2,
            }}
          >
            Zolang Nexora UI
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
            }}
          >
            A beautiful, responsive React UI component library built with Material-UI
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleGetStartedClick} // Fixed: Added click handler
              startIcon={<RocketLaunchIcon />}
            >
              Get Started
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              onClick={handleViewComponentsClick} // Fixed: Added click handler
              startIcon={<PaletteIcon />}
            >
              View Components
            </Button>
          </Box>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={3} sx={{ mt: 6 }}>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<DesignServicesIcon />}
              title="Modern Design"
              description="Beautiful components with gradient backgrounds and smooth animations"
              gradient
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<CodeIcon />}
              title="Easy to Use"
              description="Simple API with intuitive props. Just import and start building"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<IntegrationInstructionsIcon />}
              title="Fully Customizable"
              description="Extensive theming support and customizable components"
            />
          </Grid>
        </Grid>
      </Section>

      {/* Documentation Content */}
      <Section spacing={6}>
        <Container>
          {/* Tabs Navigation */}
          <Paper 
            sx={{ 
              background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
              border: '1px solid rgba(139, 0, 0, 0.3)',
              borderRadius: 3,
              p: 2,
              mb: 4,
            }}
          >
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    color: '#ff4d4d',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#ff4d4d',
                  height: 3,
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
          </Paper>

          {/* Tab Content */}
          <Box sx={{ mt: 4 }}>
            {/* Getting Started Tab */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h3" gutterBottom sx={{ color: 'white', mb: 4 }}>
                  🚀 Getting Started
                </Typography>

                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 4,
                    background: 'rgba(139, 0, 0, 0.1)',
                    border: '1px solid rgba(139, 0, 0, 0.3)',
                    color: 'white',
                    '& .MuiAlert-icon': { color: '#ff4d4d' }
                  }}
                >
                  Make sure you have React and Material-UI installed in your project.
                </Alert>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          1. Installation
                        </Typography>
                        <Box 
                          component="pre" 
                          sx={{ 
                            background: '#0a1128',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            color: '#ff4d4d',
                            border: '1px solid rgba(139, 0, 0, 0.3)',
                          }}
                        >
                          {`npm install @zolang-nexora/ui-library`}
                        </Box>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                          Or if using locally:
                        </Typography>
                        <Box 
                          component="pre" 
                          sx={{ 
                            background: '#0a1128',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            color: '#ff4d4d',
                            border: '1px solid rgba(139, 0, 0, 0.3)',
                            mt: 1,
                          }}
                        >
                          {`npm install ./path-to-ui-library`}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          2. Setup Theme
                        </Typography>
                        <Box 
                          component="pre" 
                          sx={{ 
                            background: '#0a1128',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            color: '#ff4d4d',
                            border: '1px solid rgba(139, 0, 0, 0.3)',
                            fontSize: '0.9rem',
                          }}
                        >
                          {`import { ThemeProvider } from '@mui/material';
import { theme } from '@zolang-nexora/ui-library';

function App() {
  return (
    <ThemeProvider theme={theme}>
      <YourApp />
    </ThemeProvider>
  );
}`}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          3. Use Components
                        </Typography>
                        <Box 
                          component="pre" 
                          sx={{ 
                            background: '#0a1128',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            color: '#ff4d4d',
                            border: '1px solid rgba(139, 0, 0, 0.3)',
                            fontSize: '0.9rem',
                          }}
                        >
                          {`import { Button, FeatureCard, Section } from '@zolang-nexora/ui-library';

function HomePage() {
  return (
    <Section title="Welcome">
      <Button variant="contained" animate>
        Get Started
      </Button>
      <FeatureCard
        title="Feature"
        description="Amazing feature description"
      />
    </Section>
  );
}`}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Components Tab */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h3" gutterBottom sx={{ color: 'white', mb: 4 }}>
                  🧩 Components
                </Typography>

                <Grid container spacing={4}>
                  {components.map((component, index) => (
                    <Grid item xs={12} key={index}>
                      <Card 
                        sx={{ 
                          background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                          border: '1px solid rgba(139, 0, 0, 0.3)',
                          '&:hover': {
                            border: '1px solid rgba(139, 0, 0, 0.5)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h4" sx={{ color: '#ff4d4d', mr: 2 }}>
                              {component.name}
                            </Typography>
                            <Chip 
                              label="Component" 
                              size="small" 
                              sx={{ 
                                background: 'rgba(139, 0, 0, 0.3)',
                                color: '#ff4d4d',
                              }} 
                            />
                          </Box>
                          
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3 }}>
                            {component.description}
                          </Typography>

                          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            Props:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                            {component.props.map((prop, propIndex) => (
                              <Chip
                                key={propIndex}
                                label={prop}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  borderColor: '#ff4d4d',
                                  color: '#ff4d4d',
                                }}
                              />
                            ))}
                          </Box>

                          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            Usage:
                          </Typography>
                          <Box 
                            component="pre" 
                            sx={{ 
                              background: '#0a1128',
                              p: 2,
                              borderRadius: 1,
                              overflow: 'auto',
                              color: '#ff4d4d',
                              border: '1px solid rgba(139, 0, 0, 0.3)',
                              fontSize: '0.9rem',
                            }}
                          >
                            {component.usage.trim()}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Theming Tab */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h3" gutterBottom sx={{ color: 'white', mb: 4 }}>
                  🎨 Theming
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          Color Palette
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, background: '#0a1128', borderRadius: 1 }} />
                            <Typography sx={{ color: 'white' }}>Primary Dark: #0a1128</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, background: '#1a1f3d', borderRadius: 1 }} />
                            <Typography sx={{ color: 'white' }}>Primary Light: #1a1f3d</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, background: '#8b0000', borderRadius: 1 }} />
                            <Typography sx={{ color: 'white' }}>Secondary: #8b0000</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, background: '#ff4d4d', borderRadius: 1 }} />
                            <Typography sx={{ color: 'white' }}>Accent: #ff4d4d</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          Custom Theme
                        </Typography>
                        <Box 
                          component="pre" 
                          sx={{ 
                            background: '#0a1128',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            color: '#ff4d4d',
                            border: '1px solid rgba(139, 0, 0, 0.3)',
                            fontSize: '0.8rem',
                          }}
                        >
                          {`import { createTheme } from '@mui/material';

const customTheme = createTheme({
  palette: {
    primary: { main: '#0a1128' },
    secondary: { main: '#8b0000' },
    background: {
      default: '#0a1128',
      paper: '#1a1f3d',
    },
  },
  // Add your customizations
});`}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>
                    Progress Bar Example
                  </Typography>
                  <Box sx={{ maxWidth: 400 }}>
                    <ProgressBar value={75} label="Development Progress" color="#ff4d4d" />
                    <ProgressBar value={90} label="Design Completion" color="#8b0000" />
                    <ProgressBar value={60} label="Documentation" color="#ff6b6b" />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Examples Tab */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h3" gutterBottom sx={{ color: 'white', mb: 4 }}>
                  💡 Examples
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          Button Variants
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                          <Button variant="contained" size="small">
                            Default
                          </Button>
                          <Button variant="contained" animate size="small">
                            Animated
                          </Button>
                          <Button variant="contained" glow size="small">
                            Glowing
                          </Button>
                          <Button variant="outlined" size="small">
                            Outlined
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          Input Field
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Input label="Your Name" fullWidth />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card sx={{ background: 'rgba(26, 31, 61, 0.6)', border: '1px solid rgba(139, 0, 0, 0.2)' }}>
                      <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d' }}>
                          Stats Showcase
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12} sm={4}>
                            <StatsCard number="99" suffix="%" label="Satisfaction" animate />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <StatsCard number="24" suffix="/7" label="Support" />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <StatsCard number="1M" suffix="+" label="Users" animate />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Container>
      </Section>

      {/* Footer Section */}
      <Section background="gradient" spacing={4}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4 }}>
            Start building beautiful applications with Zolang Nexora UI components
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            glow
            onClick={handleGitHubClick} // Fixed: Added click handler
          >
            View GitHub Repository
          </Button>
        </Box>
      </Section>
    </>
  );
};

export default DocumentationPage;